import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ICustomFieldAnswer,
  ICustomFieldFormItem,
} from "../../../../@types/CustomField";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { TextField } from "../../../../components/TextField";
import { Toggle } from "../../../../components/Toggle";
import { validateCustomFields } from "../../../../utils/customFields";
import {
  DATE_VALUE_FORMAT,
  getPortalDateFormat,
} from "../../../../utils/portalDateFormat";
import {
  CustomFieldEditorContainer,
  CustomFieldEditorError,
  ToggleRow,
} from "./CustomFieldCardEditor.styles";
import type { CustomFieldCardMeta } from "./PatientMenuCard";
// The same compact input treatments the built-in cards use, so a custom field's editor is the
// same size and spacing as its neighbours instead of the TextField/Select defaults (200x42).
import {
  multiSelectCss,
  selectCss,
  textFieldCss,
} from "./PatientMenuCard.styles";

type CustomFieldCardEditorProps = {
  field: CustomFieldCardMeta;
  value: ICustomFieldAnswer;
  onChange: (answer: ICustomFieldAnswer) => void;
  // Reports whether this field currently has a validation error, so the parent can disable Save.
  onError: (hasError: boolean) => void;
};

function toOptions(options: string[]): IOption[] {
  return options.map((option) => ({
    label: option,
    value: option,
    key: option,
  }));
}

// EN-5067: inline editor for a custom-field card during an Edit session. Self-controls its value
// (seeded once from `value`), reports changes via `onChange`, and mirrors the server validation
// client-side (UX only), surfacing errors inline and via `onError`.
export function CustomFieldCardEditor({
  field,
  value,
  onChange,
  onError,
}: CustomFieldCardEditorProps) {
  const { t } = useTranslation();

  const [answer, setAnswer] = useState<ICustomFieldAnswer>(value);
  const [error, setError] = useState<string>("");

  // Reuse the shared validator by shaping the card meta into a form item.
  const validate = (next: ICustomFieldAnswer): string => {
    const item: ICustomFieldFormItem = {
      customFieldDefinitionId: field.definitionId,
      label: field.label,
      key: "",
      type: field.type,
      options: field.options,
      validation: field.validation,
      isRequired: field.isRequired,
      displayOrder: 0,
    };
    const errors = validateCustomFields([item], {
      [field.definitionId]: next,
    });
    return errors[field.definitionId] ?? "";
  };

  // Validate the initial value on mount so an empty required field disables Save immediately, and
  // clear the parent error flag when this editor unmounts (Edit cancelled / card hidden).
  useEffect(() => {
    const message = validate(answer);
    setError(message);
    onError(message !== "");
    return () => onError(false);
  }, []);

  const handleChange = (next: ICustomFieldAnswer) => {
    setAnswer(next);
    onChange(next);
    const message = validate(next);
    setError(message);
    onError(message !== "");
  };

  const renderWidget = () => {
    const inputName = `custom-field-${field.definitionId}`;

    switch (field.type) {
      case "YesNo":
        return (
          <ToggleRow>
            <Toggle
              checked={answer.value === "true"}
              onCheckedChange={(checked: boolean) =>
                handleChange({ value: checked ? "true" : "false" })
              }
            />
          </ToggleRow>
        );

      // EN-5272: the portal's date picker, emitting the server format (YYYY-MM-DD) the shared
      // validator and the PUT expect. It used to be a text box whose mask forced DD/MM/… while
      // the validator wanted YYYY-MM-DD, so no typed date could pass. Future dates stay
      // selectable — a custom date can be an expiry or review date.
      case "Date": {
        const date = answer.value
          ? dayjs(answer.value, DATE_VALUE_FORMAT, true)
          : null;

        return (
          <DateTimePickerInput
            id={inputName}
            showTime={false}
            format={getPortalDateFormat()}
            placeholder={getPortalDateFormat()}
            width="143px"
            height="28px"
            value={date?.isValid() ? date : null}
            // Clearing hands back null despite the signature, hence the guard.
            onChange={(next: Dayjs | null) =>
              handleChange({
                value: next?.isValid() ? next.format(DATE_VALUE_FORMAT) : "",
              })
            }
          />
        );
      }

      case "Integer":
      case "Decimal":
        return (
          <TextField
            label=""
            name={inputName}
            type="number"
            className={textFieldCss()}
            value={answer.value ?? ""}
            onChange={(event) => handleChange({ value: event.target.value })}
            onKeyDown={(evt) =>
              ["e", "E", "+"].includes(evt.key) && evt.preventDefault()
            }
          />
        );

      case "Dropdown":
        return (
          <SelectDropdown
            key={field.definitionId}
            options={toOptions(field.options)}
            value={answer.value}
            width={135}
            className={selectCss()}
            placeholder={t("addPatient_placeholders_chooseField", {
              field: field.label.toLowerCase(),
            })}
            onValueChange={(selected: string | string[]) =>
              handleChange({ value: selected.toString() })
            }
          />
        );

      case "Multiselect":
        return (
          <SelectDropdown
            multiple
            options={toOptions(field.options)}
            value={answer.selectedOptions ?? []}
            width={135}
            className={multiSelectCss()}
            placeholder={t("addPatient_placeholders_chooseField", {
              field: field.label.toLowerCase(),
            })}
            onValueChange={(selected: string | string[]) =>
              handleChange({
                selectedOptions: Array.isArray(selected)
                  ? selected
                  : [selected],
              })
            }
          />
        );

      case "FreeText":
      default:
        return (
          <TextField
            label=""
            name={inputName}
            className={textFieldCss()}
            value={answer.value ?? ""}
            onChange={(event) => handleChange({ value: event.target.value })}
          />
        );
    }
  };

  return (
    <CustomFieldEditorContainer>
      {renderWidget()}
      {error ? <CustomFieldEditorError>{error}</CustomFieldEditorError> : null}
    </CustomFieldEditorContainer>
  );
}
